import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Which flow the concierge sends when somebody asks it to build them a form.
 *
 * <p>The routing reads the job off the flow itself ({@code agentMeta.source}), not off its name,
 * and until now nothing showed which flow held it. On 2026-09-21 that cost a real lead: keyword
 * triggers pointed at the same form, the completion came back unrecognised, and the person had
 * their own answers echoed at them instead of being built a flow. Nothing on any screen said
 * what was supposed to be happening, so nothing said it had stopped.
 */
const IntakeFlowPanel = () => {
  const { authenticatedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [contact, setContact] = useState('');
  const [replaying, setReplaying] = useState(false);
  const [replayResult, setReplayResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/admin/intake-flow`);
      if (!response.ok) throw new Error('Could not read the intake form setting');
      const body = await response.json();
      setData(body);
      setChoice(body.current?.[0]?.id || '');
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // authenticatedFetch is recreated every AuthContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/admin/intake-flow`, {
        method: 'PUT',
        body: JSON.stringify({ flowId: choice }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || body.message || 'Could not set the intake form');
      }
      const body = await response.json();
      setData(body);
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const replay = async () => {
    setReplaying(true);
    setReplayResult(null);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/admin/intake-flow/replay`, {
        method: 'POST',
        body: JSON.stringify({ contact: contact.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.detail || body.message || 'Could not rebuild from that intake');
      }
      setReplayResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setReplaying(false);
    }
  };

  const current = data?.current || [];
  const candidates = data?.candidates || [];
  const changed = choice && choice !== (current[0]?.id || '');

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="intake-flow-panel">
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Concierge intake form
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The flow the concierge sends when somebody asks it to build them a form. Completing this
        one starts a build; completing any other flow does not.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && !changed && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}

      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Currently
            </Typography>
            {current.length === 0 ? (
              <Alert severity="warning" data-testid="no-intake-flow">
                No flow is set as the intake form. The concierge will send one the first time it
                is asked, but nothing completing a form will start a build until then.
              </Alert>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {current.map((flow) => (
                  <Chip
                    key={flow.id}
                    label={`${flow.name}${flow.status ? ` · ${flow.status.toLowerCase()}` : ''}`}
                    color={current.length > 1 ? 'warning' : 'success'}
                    variant="outlined"
                    data-testid={`current-intake-${flow.id}`}
                  />
                ))}
              </Stack>
            )}
            {current.length > 1 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                More than one flow holds this job. Pick the one that should keep it, the others
                will be released.
              </Alert>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <TextField
              select
              size="small"
              label="Use this flow"
              value={choice}
              onChange={(event) => { setChoice(event.target.value); setSaved(false); }}
              sx={{ minWidth: 280 }}
              disabled={candidates.length === 0}
              helperText={candidates.length === 0
                ? 'No flows found on the concierge number’s environment.'
                : 'Flows on the concierge number’s environment.'}
              inputProps={{ 'aria-label': 'Use this flow as the intake form' }}
            >
              {candidates.map((flow) => (
                <MenuItem key={flow.id} value={flow.id}>
                  {flow.name}{flow.status ? ` · ${flow.status.toLowerCase()}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={save}
              disabled={!changed || saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
              sx={{ flexShrink: 0, mt: { sm: 0.5 } }}
            >
              Use as intake form
            </Button>
          </Stack>

          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Build again from someone's intake
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              When somebody filled the form in and no flow came back. Builds from their most
              recent submission and sends it to them, exactly as the concierge would have.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                size="small"
                label="Their WhatsApp number"
                placeholder="5511933924934"
                value={contact}
                onChange={(event) => { setContact(event.target.value); setReplayResult(null); }}
                sx={{ minWidth: 240 }}
                inputProps={{ 'aria-label': 'Their WhatsApp number' }}
              />
              <Button
                variant="outlined"
                onClick={replay}
                disabled={!contact.trim() || replaying}
                startIcon={replaying ? <CircularProgress size={16} /> : null}
                sx={{ flexShrink: 0, mt: { sm: 0.5 } }}
              >
                Build and send
              </Button>
            </Stack>
            {replayResult && (
              <Alert
                severity={replayResult.built ? 'success' : 'warning'}
                sx={{ mt: 1.5 }}
                data-testid="replay-result"
              >
                {replayResult.detail}
              </Alert>
            )}
          </Box>

          {data?.conciergeWabaId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Concierge number: WABA {data.conciergeWabaId}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default IntakeFlowPanel;
